/**
 * The screen the workspace shows while it loads.
 *
 * Booting is not instant — the connected clients app is a real round trip to
 * another host and takes a couple of seconds on its own — so this is on screen
 * long enough to be looked at. A bare spinner on an empty page reads as
 * "something is wrong"; a mark, a line that is visibly moving, and a word
 * about what is happening reads as "nearly there".
 *
 * Deliberately no percentage: the loads run in parallel and finish out of
 * order, so any number here would be invented, and an invented number that
 * sticks at 80% is worse than none.
 */
import { useEffect, useState } from 'react';
import Icon from '../lib/icons.jsx';

/*
 * What it is waiting on, in the order it tends to finish. Swapped on a timer
 * rather than wired to the actual loads: the point is to show that time is
 * passing for a reason, and threading real progress through every loader to
 * say so would be a lot of plumbing for a two-second screen.
 */
const NOTES = [
  'Loading your workspace…',
  'Fetching your accounts…',
  'Bringing the pipeline across…',
  'Almost there…'
];

export default function BootScreen() {
  const [note, setNote] = useState(0);

  useEffect(() => {
    /* Stops on the last one rather than looping — a message that cycles
       forever says the app is stuck even when it is not. */
    const t = setInterval(
      () => setNote(n => (n < NOTES.length - 1 ? n + 1 : n)),
      1100
    );
    return () => clearInterval(t);
  }, []);

  return (
    <div className="boot" role="status" aria-live="polite">
      <div className="bootmark">
        <span className="bootring" aria-hidden="true" />
        <span className="bootlogo"><Icon name="zap" /></span>
      </div>

      <div className="boottext">
        <b>Client Management</b>
        <span>{NOTES[note]}</span>
      </div>

      <div className="bootbar" aria-hidden="true"><i /></div>
    </div>
  );
}
